/*
  ImportPauseBanner — the emergency-stop control for document /
  exercise-program imports.

  Renders a persistent banner at the top of the pages that trigger
  bulk imports (Document Imports + Bulk Programs). Two states:

    * Not paused → red "Stop all imports" button. Confirms and calls
      POST /document-imports/stop-all, which flips a persisted
      Mongo flag AND cancels every queued item + processing batch.
    * Paused    → yellow banner showing when/who paused it, plus a
      Resume button that clears the flag (does not re-queue anything).

  Uses the persistent flag so it survives pod restarts and both
  pages stay in sync when the state changes elsewhere.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { StopCircle, Loader2, ShieldAlert, PlayCircle } from "lucide-react";


export default function ImportPauseBanner({ onChanged }) {
  const [state, setState] = useState({ paused: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/document-imports/pause-state");
      setState(r.data || { paused: false });
    } catch (e) {
      // Non-fatal — silently degrade
      console.warn("pause-state load failed", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);   // stay in sync across tabs
    return () => clearInterval(t);
  }, [load]);

  async function stopAll() {
    if (!window.confirm(
      "STOP ALL IMPORTS?\n\n" +
      "This will:\n" +
      "  • cancel every queued file across every batch\n" +
      "  • flip processing batches to Cancelled\n" +
      "  • block any new scan from starting until you tap Resume\n\n" +
      "Files already in flight will finish (few seconds each). " +
      "Approved / pending / rejected documents are preserved.",
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/document-imports/stop-all");
      toast.warning("All imports stopped", {
        description: `Cancelled ${r.data.cancelled_items} file(s) across ` +
          `${r.data.cancelled_batches} batch(es). Imports are paused globally.`,
      });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Stop failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setBusy(false); }
  }

  async function resumeAll() {
    setBusy(true);
    try {
      await api.post("/document-imports/resume-all");
      toast.success("Imports unpaused — new scans can start now.");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Resume failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setBusy(false); }
  }

  if (loading) return null;

  if (state.paused) {
    const when = state.paused_at ? state.paused_at.slice(0, 19).replace("T", " ") : "";
    const who = state.paused_by?.name || state.paused_by?.email || "unknown";
    return (
      <div
        className="rounded-md border border-amber-400 bg-amber-50 p-3 mb-3 flex items-center gap-3"
        data-testid="imports-paused-banner"
      >
        <ShieldAlert className="h-5 w-5 text-amber-700 flex-shrink-0" />
        <div className="flex-1 text-sm text-amber-900">
          <b>Imports are paused globally.</b>{" "}
          {when && <span className="text-2xs">Paused by {who} · {when}</span>}
          <div className="text-2xs text-amber-800 mt-0.5">
            Workers exit before claiming any more files. Approved / pending
            documents are preserved. New scans are blocked.
          </div>
        </div>
        <Button
          size="sm" onClick={resumeAll} disabled={busy}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="imports-resume-all-btn"
        >
          {busy
            ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Resuming…</>
            : <><PlayCircle className="h-3.5 w-3.5 mr-1" /> Resume imports</>}
        </Button>
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-red-200 bg-red-50/50 p-2 mb-3 flex items-center gap-2 text-[0.75rem]"
      data-testid="imports-stop-bar"
    >
      <ShieldAlert className="h-4 w-4 text-red-700 flex-shrink-0" />
      <span className="flex-1 text-red-900">
        Runaway import? Hit stop to cancel every queued file and pause
        new scans across the whole app.
      </span>
      <Button
        size="sm" variant="destructive" onClick={stopAll} disabled={busy}
        className="h-7 text-2xs"
        data-testid="imports-stop-all-btn"
      >
        {busy
          ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Stopping…</>
          : <><StopCircle className="h-3 w-3 mr-1" /> Stop all imports</>}
      </Button>
    </div>
  );
}
